/**
 * belleNarrativeScript.ts
 * Belle's alternate narrative script in modern teen/kid voice.
 *
 * When Belle is selected, all barks, intro lines, boss taunts (responses),
 * world complete text, and ending cutscene shift to her personality:
 * casual, slang-heavy, excitable, and Gen-Alpha-coded.
 *
 * Boss taunts themselves stay the same (bosses don't change voice) but
 * Belle's intro and bark responses are in her voice.
 *
 * @module belleNarrativeScript
 */

import type { NarrativeScript } from '../types/narrative';

/** Belle's complete narrative override — same structure as the Nova script. */
export const BELLE_NARRATIVE_SCRIPT: NarrativeScript = {
  // ─────────────────────────────────────────────────────────────
  // INTRO CUTSCENE — Belle version
  // ─────────────────────────────────────────────────────────────
  intro: {
    lines: [
      {
        speaker: 'DISPATCHER',
        text: 'Class Seven Starfall event detected. Constellation lines failing across all sectors.',
      },
      {
        speaker: 'DISPATCHER',
        text: 'Whole regions are disappearing from the star-map. We need volunteers—',
      },
      {
        speaker: 'BELLE',
        text: 'Say less bro, I\'m literally already running. Let\'s gooo!',
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────
  // HUB WORLD MAP — LORE LINES (Belle voice)
  // ─────────────────────────────────────────────────────────────
  worldLoreLines: {
    1: {
      worldId: 1,
      worldName: 'Sunmeadow Cliffs',
      constellation: 'Dawn Constellation',
      line: 'Sunmeadow vibes. Some unhinged rooster went full chaos mode on the star-lines. Gotta fix the Dawn Constellation fr.',
    },
    2: {
      worldId: 2,
      worldName: 'Starfield Drift',
      constellation: 'Drift Constellation',
      line: 'Okay the star-routes are literally in pieces so we\u2019re flying the ship through open space. Drift Constellation fragments everywhere, this is so chaotic I love it.',
    },
    3: {
      worldId: 3,
      worldName: 'Brasscloud Foundry',
      constellation: 'Forge Constellation',
      line: 'Brasscloud Foundry is so extra rn. The machines are literally hammering stars into sparks. Not on my watch bestie.',
    },
    4: {
      worldId: 4,
      worldName: 'Crystal Tide Ruins',
      constellation: 'Prism Tide Constellation',
      line: 'These ruins are lowkey gorgeous but the water is bending all the stars wrong. That\'s an L, we\'re fixing the Prism Tide Constellation.',
    },
    5: {
      worldId: 5,
      worldName: 'Eclipse Bastion',
      constellation: 'Crown of Night',
      line: 'Okay this place is giving final boss energy and I\'m here for it. The Star-Eater is NOT eating today, no cap.',
    },
  },

  // ─────────────────────────────────────────────────────────────
  // BOSS INTRO TAUNTS — bosses keep their voice, same as Nova script
  // ─────────────────────────────────────────────────────────────
  bossTaunts: {
    1: {
      worldId: 1,
      bossName: 'The Gale Rooster',
      taunt: 'Chickens don\'t belong in the sky, courier.',
    },
    2: {
      worldId: 2,
      bossName: 'Void Mothership',
      taunt: 'One small ship against the fleet. How\u2026 courier of you.',
    },
    3: {
      worldId: 3,
      bossName: 'King Rivet',
      taunt: 'Unauthorized courier detected. Preparing permanent repair.',
    },
    4: {
      worldId: 4,
      bossName: 'The Prism Leviathan',
      taunt: 'You chase reflections, little runner. Which one is really you?',
    },
    5: {
      worldId: 5,
      bossName: 'The Star-Eater',
      taunt: 'Run all you like, courier. Every road ends in my dark.',
    },
  },

  // ─────────────────────────────────────────────────────────────
  // WORLD COMPLETE LINES (Belle voice)
  // ─────────────────────────────────────────────────────────────
  worldCompleteTexts: {
    1: {
      worldId: 1,
      text: 'That rooster was unhinged but we cleared it. First constellation back, massive W.',
    },
    2: {
      worldId: 2,
      text: 'Drift cleared, star-routes are humming again, and we\u2019re banking toward the next world. Actual main character energy.',
    },
    3: {
      worldId: 3,
      text: 'Foundry cleared, gears are chilling, Forge Constellation is back online. Actual slay.',
    },
    4: {
      worldId: 4,
      text: 'Tide cleared, ruins are singing, and the stars finally point home. That\'s so fire.',
    },
    5: {
      worldId: 5,
      text: 'The last fracture closed. Sky remembers its shape. We literally just saved everything ong.',
    },
  },

  // ─────────────────────────────────────────────────────────────
  // FINAL ENDING CUTSCENE (Belle voice)
  // ─────────────────────────────────────────────────────────────
  ending: {
    lines: [
      {
        speaker: 'BELLE',
        text: 'Bro. We actually did it. Every single star, back where it belongs.',
      },
    ],
    closingText:
      'The routes are open. The stars are whole. And wherever the sky calls next, Belle is already running.',
  },

  // ─────────────────────────────────────────────────────────────
  // BELLE BARKS — teen slang, high energy
  // ─────────────────────────────────────────────────────────────
  novaBarkSet: {
    checkpointActivate: [
      'Bet, locked in.',
      'Saved, no cap.',
      'Good anchor fr.',
      'Marked it. Moving.',
      'That\'ll hold, bet.',
    ],

    shardCollected: [
      'Yooo there\'s one!',
      'Sky piece secured, W.',
      'One more for the map bro.',
      'The stars remember this shape ong.',
      'It\'s lowkey warm?? Kinda cute.',
    ],

    seedMilestone: [
      'Seeds adding up, let\'s gooo.',
      'Keep em coming fr fr.',
      'Pockets heavy, no cap.',
      'Every seed counts bestie.',
    ],

    secretRoomFound: [
      'Wait... this was NOT on the map bro.',
      'Someone hid this?? Sus.',
      'Off-route and so worth it, slay.',
      'Good thing I looked twice ngl.',
    ],

    bossDefeated: {
      1: 'Lmao sit down rooster, that\'s a massive L for you.',
      2: 'One whole fleet?? And my boots feel SO bouncy now, no cap.',
      3: 'Stay bolted down next time bro, you were NOT it.',
      4: 'Stop chasing reflections, that\'s actually embarrassing.',
      5: 'The dark has an end after all. We cleared it, ong.',
    },

    lowHealth: [
      'Bruh. Okay. FOCUS.',
      'Not today bro, NOT today.',
      'Still standing, no cap.',
      'One more hit and I\'m literally cooked.',
    ],
  },
};
